/// <amd-module name="Wit/WorkItemTFVCLinks/Components/VersionedItemLinkForm" />
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { format } from "VSS/Core/Util/String";
import { useDebouncedSubscription, useObservable, useSubscription } from "VSS/Core/Observable";
import { IListBoxItem } from "VSSUI/ListBox";
import { FormItem } from "VSSUI/FormItem";
import { Dropdown } from "VSSUI/Dropdown";
import { Observer } from "VSSUI/Observer";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { ArtifactsUpdatedCallback } from "Wit/WorkItemLinksRegistration/IRegisteredLinkTypeService";
import { usePageContext } from "VSS/Platform/Layout";
import { TfvcChangesetRef, TfvcVersionOption, TfvcVersionType, VersionControlRecursionType } from "VC/Common/Contracts/Git";
import { getTfvcClient } from "VC/Common/RestClient/Tfvc";
import * as Resources from "Wit/WorkItemTFVCLinks/Resources";
import { latestVersion, versionedItemToDisplayData } from "../TFVCLinkUtils";
import { ItemPathInput } from "./ItemPathInput";
import { ChangesetPicker } from "./ChangesetPicker";

type ItemVersionType = "latest" | "changeset";
const itemVersionTypeOptions: IListBoxItem<ItemVersionType>[] = [
    { id: "latest", data: "latest", text: Resources.LinkToLatestVersionOption },
    { id: "changeset", data: "changeset", text: Resources.LinkToChangesetOption }
];

interface IVersionedItemLinkFormProps {
    onArtifactsUpdated: ArtifactsUpdatedCallback;
}

export function VersionedItemLinkForm({ onArtifactsUpdated }: IVersionedItemLinkFormProps) {
    const pageContext = usePageContext();
    const client = useMemo(() => getTfvcClient(pageContext), [pageContext]);
    const [path, setPath] = useObservable("");
    const [changeset, setChangest] = useObservable<TfvcChangesetRef | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useObservable<string | undefined>(undefined);

    const [isLinkingToChangeset, setLinkingToChangeset] = useObservable<boolean>(false);
    const versionTypeSelection = useMemo(() => new DropdownSelection(), []);

    /**
     * Initially choose to look for the latest version of the specified item
     */
    useEffect(() => {
        versionTypeSelection.select(0);
    }, []);

    /**
     * Find a versioned item matching the criteria, or show an error if none can be found.
     */
    const onChangesetOrPathChanged = useCallback(async () => {
        const newPath = path.value;
        const newChangeset = changeset.value;
        const linkingToChangeset = isLinkingToChangeset.value;

        if (!newPath) {
            onArtifactsUpdated([]);
            setErrorMessage(Resources.ErrorMissingItemPath);
            return;
        }

        if (linkingToChangeset && !newChangeset) {
            onArtifactsUpdated([]);
            // The changeset picker handles this error message
            return;
        }

        const version = newChangeset ? newChangeset.changesetId : latestVersion;
        const newItem = await client
            .getItem(
                newPath,
                undefined,
                undefined,
                false,
                undefined,
                VersionControlRecursionType.None,
                {
                    version: version.toString(),
                    versionType: newChangeset ? TfvcVersionType.Changeset : TfvcVersionType.Latest,
                    versionOption: TfvcVersionOption.None
                },
                false
            )
            .catch(() => undefined);

        if (newPath !== path.value || newChangeset !== changeset.value) {
            // The form has changed since sending the request ; do nothing with the response
            return;
        }

        if (!newItem) {
            onArtifactsUpdated([]);
            setErrorMessage(format(Resources.ErrorCannotFindVersionedItem, newPath));
            return;
        }

        onArtifactsUpdated([versionedItemToDisplayData(pageContext, newItem, version)]);
        setErrorMessage(undefined);
    }, [pageContext, client]);

    useSubscription(changeset, onChangesetOrPathChanged, []);
    useDebouncedSubscription(path, 300, onChangesetOrPathChanged, []);

    return (
        <>
            <Observer path={path}>
                {({ path }: { path: string }) => (
                    <ItemPathInput className="margin-bottom-8" itemPath={path} onItemPathChanged={setPath} errorMessage={errorMessage} />
                )}
            </Observer>

            <FormItem label={Resources.LinkToVersionTypeLabel}>
                <Dropdown
                    items={itemVersionTypeOptions}
                    selection={versionTypeSelection}
                    onSelect={(e, item) => {
                        if (item.data === "changeset") {
                            setLinkingToChangeset(true);
                        } else {
                            setLinkingToChangeset(false);
                            setChangest(undefined);
                        }
                        onChangesetOrPathChanged();
                    }}
                />
            </FormItem>

            <ConditionalChildren renderChildren={isLinkingToChangeset}>
                <ChangesetPicker onChangesetChanged={setChangest} />
            </ConditionalChildren>
        </>
    );
}
